/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGlobalSearch, useProcess } from "@/global-data";
import { useDeletePopup } from "@/global-data/use-delete-popup";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useToast } from "@/hooks/use-toast";
import { getUserInfo } from "@/lib/token";
import { wait } from "@/lib/wait";
import { apiServices } from "@/services/api.services";
import { retailer_group, retailers } from "@/services/global.services";
import { useEffect, useMemo, useState } from "react";

export const useMutations = () => {
  const { toast } = useToast();

  const API_SERVICE = retailer_group;
  const { company_id, user_id } = useMemo(() => {
    const tempUserData = getUserInfo();
    return {
      company_id: tempUserData?.company_id,
      user_id: tempUserData?.user_id || "",
    };
  }, []);
  const { setProcess } = useProcess();
  const [skeltonLoading, setSkeltonLoading] = useState(true);
  const [retailerList, setRetailerList] = useState([]);
  const [listData, setListData] = useState([]);
  const [formPopupOpen, setFormPopupOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(undefined);
  const [activeRecordData, setActiveRecordData] = useState<any>(undefined);

  const { setSearchProgress, search } = useGlobalSearch();
  const [searchDebounced] = useDebouncedValue(search, 200);

  const { onDelete, status, identity, clearDelete } = useDeletePopup();

  const [sortBy, setSortBy] = useState("cts");
  const [orderBy, setOrderBy] = useState(false);

  const [paginationData, setPaginationData] = useState({
    page: 1,
    perPage: 10,
    totalItem: 1,
  });

  //On Load Component
  useEffect(() => {
    (async () => {
      await loadingList({ searchText: "" });
      await loadingRetailer();
      await wait(1000);
      setSkeltonLoading(false);
      setProcess(false);
    })();
  }, []);

  //handle delete
  useEffect(() => {
    if (status === "cancel") {
      setDeleteId(undefined);
      clearDelete();
    }
    if (status === "delete" && identity === "user") {
      (async () => {
        setProcess(true);
        const deleteResponse: any = await deleteRecord();
        if (deleteResponse?.data) {
          await loadingList({});
          toast({
            title: "Success",
            variant: "success",
            description:
              deleteResponse?.data.result.metadata.message ??
              deleteResponse?.data?.status?.msg ??
              deleteResponse?.data?.status?.message,
          });
        }
        clearDelete();
        setDeleteId(undefined);
        setProcess(false);
      })();
    }
  }, [status]);

  const deleteRecord = async () => {
    const payload = {
      data: {
        retailer_id: deleteId,
        company_id: company_id,
      },
      company_id: company_id,
    };

    const { data, error, message } = await apiServices<any>(
      API_SERVICE.delete,
      "post",
      payload,
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  //Handle Search
  useEffect(() => {
    if (skeltonLoading === false) {
      (async () => {
        setSearchProgress(true);
        setPaginationData((prev) => ({ ...prev, page: 1 }));
        await loadingList({ page: 1 });
        setSearchProgress(false);
      })();
    }
  }, [searchDebounced]);

  const onFormOpenChange = (prop: boolean) => {
    setFormPopupOpen(prop);
  };

  const onPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, page: prop }));
    setProcess(true);
    await loadingList({ page: prop });
    setProcess(false);
  };
  const onPerPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, perPage: prop, page: 1 }));
    setProcess(true);
    await loadingList({ perPage: prop, page: 1 });
    setProcess(false);
  };

  const permission: any = [];
  const onAction = async ({ action, data }: any) => {
    if (action === "edit") {
      setActiveRecordData(data);
      onFormOpenChange(true);
    }
    if (action === "delete") {
      setActiveRecordData(data);
      setDeleteId(data);
      onDelete({
        description:
          "This action cannot be undone. This will permanently delete the record and remove its data from our system.",
        id: data,
        identity: "user",
        popupStatus: true,
        title: "Are you sure you want to delete this retailer?",
      });
      // await loadingList({});
    }
  };

  useEffect(() => {
    if (formPopupOpen === false) {
      setActiveRecordData(undefined);
    }
  }, [formPopupOpen]);

  const sortingHandler = async (prop: string) => {
    const tempSortBy = prop;
    const tempOrderBy = sortBy == prop ? !orderBy : true;
    console.log({ tempSortBy, tempOrderBy });
    setProcess(true);
    setSortBy(tempSortBy);
    setOrderBy(tempOrderBy);
    await loadingList({
      sortByProp: tempSortBy,
      orderByProp: tempOrderBy,
    });
    setProcess(false);
  };

  const loadingList = async ({
    perPage,
    page,
    searchText,
    orderByProp,
    sortByProp,
  }: {
    perPage?: number;
    page?: number;
    searchText?: string;
    orderByProp?: any;
    sortByProp?: any;
  }) => {
    const page_no = page || paginationData.page;

    const tempSortBy = sortByProp ?? sortBy;
    const tempOrderBy = orderByProp ?? orderBy;

    const payload = {
      data: {
        filter_by: {
          search_text: searchText ?? searchDebounced,
          exact_match: false,
          pagination: {
            page_no: page_no,
            no_of_recs: perPage || paginationData.perPage,
            sort_by: tempSortBy,
            order_by: tempOrderBy,
          },
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      API_SERVICE.get,
      "post",
      payload,
    );

    if (data) {
      setListData(data.result.metadata[API_SERVICE.key]);
      setPaginationData((prev) => ({
        ...prev,
        totalItem: data?.result?.metadata?.total_records || 1,
      }));
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  const onFormSubmit = async (prop: any): Promise<any> => {
    const payload = {
      data: {
        retailer_groups: [prop],
      },
      company_id: company_id,
      user_id: user_id,
    };

    const { data, error, message } = await apiServices<any>(
      API_SERVICE.post,
      "post",
      payload,
    );

    if (data) {
      const response = await loadingList({});
      if (response.data) {
        toast({
          title: "Success",
          variant: "success",
          description:
            data.result.metadata.message ??
            data?.status?.msg ??
            data?.status?.message,
        });
      }
      setFormPopupOpen(false);
    }

    if (error) {
      setProcess(false);
    }

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  const loadingRetailer = async () => {
    const payload = {
      data: {
        filter_by: {
          search_text: "",
          exact_match: false,
          pagination: {
            page_no: 1,
            no_of_recs: 1000,
            sort_by: "cts",
            order_by: false,
          },
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      retailers.get,
      "post",
      payload,
    );

    if (data) {
      setRetailerList(data.result.metadata[retailers.key]);
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  return {
    skeltonLoading,
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,
    onAction,
    activeRecordData,
    onFormSubmit,
    onFormOpenChange,
    formPopupOpen,
    retailerList,
    sortingHandler,
    sortBy,
    orderBy,
  };
};
